/** Placeholder three.js renderer: flat colored geometry, capsule units, translucent telegraphs. */
import * as THREE from 'three';
import type { DungeonDef, UnitId, Vec2 } from '@keystone/sim';
import type { CameraRig } from '../camera';
import type { Renderer, RenderFrame, RenderUnit, ScreenPoint } from '../types';
import { AreaView } from './areas';
import { fromThree, toThree } from './coords';
import { buildDungeon } from './dungeon';
import { FxLayer } from './fx';
import { SelectionRing, UnitView } from './units';

export function createPlaceholderRenderer(container: HTMLElement): Renderer {
  const gl = new THREE.WebGLRenderer({ antialias: true });
  gl.setPixelRatio(Math.min(2, window.devicePixelRatio));
  gl.domElement.style.display = 'block';
  container.appendChild(gl.domElement);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x0b0c10);
  scene.fog = new THREE.Fog(0x0b0c10, 70, 160);
  const camera = new THREE.PerspectiveCamera(50, 1, 0.5, 600);
  scene.add(new THREE.HemisphereLight(0xdfe8ff, 0x20242c, 1.1));
  const sun = new THREE.DirectionalLight(0xffffff, 1.2);
  sun.position.set(30, 80, 20);
  scene.add(sun);

  let dungeonGroup: THREE.Group | null = null;
  const units = new Map<UnitId, UnitView>();
  const areas = new Map<string, AreaView>();
  const selectRing = new SelectionRing(0.9);
  const hoverRing = new SelectionRing(0.35);
  scene.add(selectRing.mesh, hoverRing.mesh);
  const fx = new FxLayer((id, color, until) => units.get(id)?.flash(color, until));
  scene.add(fx.group);

  const raycaster = new THREE.Raycaster();
  const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const tmp = new THREE.Vector3();
  let lastFrame: RenderFrame | null = null;

  const ndc = (sx: number, sy: number): THREE.Vector2 => {
    const r = gl.domElement.getBoundingClientRect();
    return new THREE.Vector2(((sx - r.left) / r.width) * 2 - 1, -((sy - r.top) / r.height) * 2 + 1);
  };

  function syncUnits(frame: RenderFrame): Map<UnitId, RenderUnit> {
    const byId = new Map<UnitId, RenderUnit>();
    for (const u of frame.units) {
      byId.set(u.id, u);
      let v = units.get(u.id);
      if (!v) {
        v = new UnitView(u.id, u);
        units.set(u.id, v);
        scene.add(v.group);
      }
      v.update(u, frame.timeMs);
    }
    for (const [id, v] of units) {
      if (!byId.has(id)) {
        scene.remove(v.group);
        v.dispose();
        units.delete(id);
      }
    }
    return byId;
  }

  function syncAreas(frame: RenderFrame): void {
    const seen = new Set<string>();
    for (const a of frame.areas) {
      seen.add(a.id);
      let v = areas.get(a.id);
      if (!v) {
        v = new AreaView(a);
        areas.set(a.id, v);
        scene.add(v.group);
      }
      v.update(a, frame.timeMs);
    }
    for (const [id, v] of areas) {
      if (!seen.has(id)) {
        scene.remove(v.group);
        v.dispose();
        areas.delete(id);
      }
    }
  }

  return {
    init(dungeon: DungeonDef | undefined) {
      if (dungeonGroup) scene.remove(dungeonGroup);
      dungeonGroup = dungeon ? buildDungeon(dungeon) : null;
      if (dungeonGroup) scene.add(dungeonGroup);
    },

    render(frame, rig: CameraRig) {
      lastFrame = frame;
      const byId = syncUnits(frame);
      syncAreas(frame);
      for (const f of frame.newFx) fx.spawn(f);
      fx.update(frame.timeMs, byId, (id) => units.get(id)?.height ?? 2);

      const player = frame.playerId ? byId.get(frame.playerId) : undefined;
      const sel = frame.selectedId ? byId.get(frame.selectedId) : undefined;
      const hov = frame.hoverId && frame.hoverId !== frame.selectedId ? byId.get(frame.hoverId) : undefined;
      const hostile = (u: RenderUnit | undefined): boolean => !!u && u.faction !== (player?.faction ?? 'party');
      selectRing.place(sel, hostile(sel));
      hoverRing.place(hov, hostile(hov));

      const eye = rig.eye();
      camera.position.copy(toThree({ x: eye.x, y: eye.y }, eye.height));
      camera.lookAt(toThree(rig.state.focus, 1, tmp));
      gl.render(scene, camera);
    },

    pickGround(sx, sy) {
      raycaster.setFromCamera(ndc(sx, sy), camera);
      const hit = new THREE.Vector3();
      return raycaster.ray.intersectPlane(groundPlane, hit) ? fromThree(hit) : null;
    },

    // +field: town NPCs, celebrations and quest markers are toon-only (no-ops here).
    setNpcMarkers() {},
    celebrate() {},
    npcAt: () => null,

    pickUnit(sx, sy) {
      raycaster.setFromCamera(ndc(sx, sy), camera);
      const hits = raycaster.intersectObjects([...units.values()].map((v) => v.hitbox), false);
      const alive = new Set(lastFrame?.units.filter((u) => u.alive).map((u) => u.id) ?? []);
      const ids = hits.map((h) => h.object.userData['unitId'] as UnitId);
      return ids.find((id) => alive.has(id)) ?? ids[0] ?? null;
    },

    project(p: Vec2, height: number): ScreenPoint {
      const v = toThree(p, height, tmp.clone()).project(camera);
      const r = gl.domElement.getBoundingClientRect();
      return {
        x: r.left + ((v.x + 1) / 2) * r.width,
        y: r.top + ((1 - v.y) / 2) * r.height,
        visible: v.z < 1 && v.z > -1 && Math.abs(v.x) <= 1.1 && Math.abs(v.y) <= 1.1,
      };
    },

    unitHeight: (id) => units.get(id)?.height ?? 2,

    resize(w, h) {
      gl.setSize(w, h);
      camera.aspect = w / Math.max(1, h);
      camera.updateProjectionMatrix();
    },

    dispose() {
      fx.clear();
      for (const v of units.values()) v.dispose();
      for (const v of areas.values()) v.dispose();
      scene.traverse((o) => {
        if (o instanceof THREE.Mesh) {
          o.geometry.dispose();
          const m = o.material as THREE.Material | THREE.Material[];
          (Array.isArray(m) ? m : [m]).forEach((x) => x.dispose());
        }
      });
      gl.dispose();
      gl.domElement.remove();
    },
  };
}
